/*
  VS BOLD hero slider (sections/vsb-hero-slider): <d-hero>.

  Without this script the slides are a row that scrolls and snaps (swiped on a
  phone) with a link to each slide. With it:
  - autoplay at data-interval seconds, unless the section turns it off or the
    visitor asks for reduced motion (then it starts stopped);
  - phones never move by themselves unless data-autoplay-phone says so;
  - it pauses while the pointer is over it, while it is off the screen and
    while the tab is hidden; the pause button stops it until pressed again
    (WCAG 2.2.2), and so does any arrow, slide bar, swipe or key, and focus
    coming into it (the WAI-ARIA carousel pattern);
  - "slide" scrolls the row in 500 ms, easing out (a swipe is the browser's
    own scroll); "fade" stacks the slides and swipes with pointer events;
  - the slides not shown are inert, so the tab order and screen readers stay
    on the one on screen; the live region speaks a slide change only while
    stopped (it would talk over everything while playing);
  - each slide's picture is fetched just before it is shown; a "fade" slide
    is not laid out at all until then.
  Every word comes from the section's settings (data-label-*).
*/
if (!customElements.get('d-hero')) {
  customElements.define('d-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('.d-hero__track');
      this.slides = [...this.querySelectorAll('.d-hero__slide')];
      this.count = this.slides.length;
      if (this.count < 2 || !this.track) return;
      this.dots = [...this.querySelectorAll('.d-hero__dot')];
      this.live = this.querySelector('[data-hero="live"]');
      this.pauseBtn = this.querySelector('[data-hero="pause"]');
      this.fade = this.dataset.transition === 'fade';
      this.ms = Math.max(2, Number(this.dataset.interval) || 6) * 1000;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.phone = window.matchMedia('(max-width: 749px)');
      this.stillPhone = this.dataset.autoplayPhone !== 'true';
      this.index = 0;
      this.holds = new Set();
      this.stopped = this.dataset.autoplay !== 'true' || this.reduce.matches || (this.phone.matches && this.stillPhone);
      this.left = this.ms;

      this.classList.add('d-hero--js');
      this.querySelectorAll('[data-hero="prev"], [data-hero="next"], [data-hero="pause"]').forEach((b) => { b.hidden = false; });
      if (this.fade) this.slides.forEach((s, i) => s.classList.toggle('is-idle', i > 1));
      this.arm(1);

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKey(e));
      // A mouse over it holds it; a tap on a phone does not (it never "leaves").
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hold('hover', true); });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.hold('hover', false); });
      // Focus coming in stops it for good (APG): it starts again only from the play button.
      // (Not the pause button's own focus: its click decides.)
      this.addEventListener('focusin', (e) => {
        if (this.contains(e.relatedTarget) || this.stopped || e.target.closest('[data-hero="pause"]')) return;
        this.stop(true);
      });
      document.addEventListener('visibilitychange', () => this.hold('hidden', document.hidden));
      if (window.IntersectionObserver) {
        new IntersectionObserver((entries) => this.hold('offscreen', !entries[0].isIntersecting), { threshold: 0.25 }).observe(this);
      }
      this.reduce.addEventListener?.('change', () => { if (this.reduce.matches) this.stop(true); });
      this.phone.addEventListener?.('change', () => { if (this.phone.matches && this.stillPhone) this.stop(true); });

      if (this.fade) this.swipe();
      else {
        let t = 0;
        this.track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => this.settled(), 90); }, { passive: true });
        if (window.ResizeObserver) new ResizeObserver(() => this.align()).observe(this.track);
      }

      // The theme editor: show the slide being edited, and hold still.
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i >= 0) { this.hold('editor', true); this.go(i); }
      });
      document.addEventListener('shopify:block:deselect', () => this.hold('editor', false));

      this.show(0, false);
      this.render();
      this.schedule();
    }

    /* ---- state */
    get playing() { return !this.stopped && this.holds.size === 0; }

    hold(why, on) {
      const was = this.playing;
      if (on) this.holds.add(why); else this.holds.delete(why);
      if (was && !this.playing) this.pauseClock();
      if (!was && this.playing) this.schedule();
      this.render();
    }

    stop(stopped) {
      this.stopped = stopped;
      clearTimeout(this.timer);
      if (!stopped) { this.left = this.ms; this.restartFill(); this.schedule(); }
      this.render();
    }

    render() {
      this.classList.toggle('is-stopped', this.stopped);
      this.classList.toggle('is-playing', !this.stopped);
      this.classList.toggle('is-paused', !this.stopped && this.holds.size > 0);
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-label', this.stopped ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
      if (this.live) this.live.setAttribute('aria-live', this.stopped ? 'polite' : 'off');
    }

    /* ---- the clock */
    schedule() {
      clearTimeout(this.timer);
      if (!this.playing) return;
      this.started = Date.now();
      this.timer = setTimeout(() => { this.left = this.ms; this.go(this.index + 1); }, this.left);
    }

    pauseClock() {
      clearTimeout(this.timer);
      if (this.started) this.left = Math.max(300, this.left - (Date.now() - this.started));
    }

    restartFill() {
      const dot = this.dots[this.index];
      const fill = dot?.querySelector('.d-hero__dot-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    /* ---- moving */
    arm(i) {
      const s = this.slides[(i + this.count) % this.count];
      if (!s) return;
      s.classList.remove('is-idle');
      s.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    go(i, { user = false } = {}) {
      const to = ((i % this.count) + this.count) % this.count;
      if (user) this.stop(true);
      this.arm(to);
      this.arm(to + 1);
      if (this.fade) this.show(to, true);
      else {
        this.slideTo(to);
        this.show(to, true);
      }
    }

    show(to, announce) {
      const changed = to !== this.index;
      this.index = to;
      this.slides.forEach((s, i) => {
        const on = i === to;
        s.classList.toggle('is-active', on);
        s.inert = !on;
        if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === to);
        if (i === to) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      if (changed) { this.left = this.ms; this.restartFill(); }
      if (announce && this.live) this.live.textContent = this.slides[to].getAttribute('aria-label') || '';
      this.schedule();
    }

    // "slide": 500 ms, easing out; the snap is off while it moves, so it does not fight the animation.
    slideTo(to) {
      const t = this.track;
      const from = t.scrollLeft;
      const target = to * t.clientWidth;
      cancelAnimationFrame(this.raf);
      if (this.reduce.matches || Math.abs(from - target) < 1) { t.scrollLeft = target; return; }
      this.animating = true;
      t.style.scrollSnapType = 'none';
      const start = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - start) / 500);
        t.scrollLeft = from + (target - from) * (1 - (1 - k) ** 3);
        if (k < 1) { this.raf = requestAnimationFrame(step); return; }
        t.style.scrollSnapType = '';
        this.animating = false;
      };
      this.raf = requestAnimationFrame(step);
    }

    // "slide": the row stopped scrolling (a swipe, a scroll or our own scrollTo).
    settled() {
      if (this.animating) return;
      const w = this.track.clientWidth || 1;
      const i = Math.round(this.track.scrollLeft / w);
      if (i !== this.index && i >= 0 && i < this.count) {
        this.stop(true);
        this.arm(i + 1);
        this.show(i, true);
      }
    }

    align() {
      if (this.fade) return;
      if (this.animating) return;
      this.track.scrollLeft = this.index * this.track.clientWidth;
    }

    // "fade": a horizontal swipe changes the slide; a vertical one scrolls the page.
    swipe() {
      let x0 = null; let y0 = 0; let moved = false;
      this.track.addEventListener('pointerdown', (e) => { if (e.isPrimary) { x0 = e.clientX; y0 = e.clientY; moved = false; } });
      this.track.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0; const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { moved = true; this.go(this.index + (dx < 0 ? 1 : -1), { user: true }); }
      });
      this.track.addEventListener('pointercancel', () => { x0 = null; });
      // A swipe is not a click on the slide's link.
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); moved = false; } }, true);
    }

    /* ---- input */
    onClick(e) {
      const b = e.target.closest('[data-hero]');
      if (!b || !this.contains(b)) return;
      const what = b.dataset.hero;
      if (what === 'prev') this.go(this.index - 1, { user: true });
      else if (what === 'next') this.go(this.index + 1, { user: true });
      else if (what === 'go') { e.preventDefault(); this.go(Number(b.dataset.index) || 0, { user: true }); }
      else if (what === 'pause') this.stop(!this.stopped);
    }

    onKey(e) {
      const arrow = /^Arrow(Left|Right)$/.exec(e.key);
      if (!arrow) return;
      if (!e.target.closest('.d-hero__bar, .d-hero__track')) return;
      e.preventDefault();
      this.go(this.index + (arrow[1].length === 5 ? 1 : -1), { user: true });
    }
  });
}
